import { observer } from 'mobx-react';
import { runInAction } from 'mobx';
import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { Card } from '@monorepo/base/src/components/card/card';
import styles from './replace-segmentation-cids.module.scss';
import { PrimaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { BottomBar, BottomBarActions } from '@monorepo/base/src/components/bottom-bar/bottom-bar';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IAdminxStore } from '../../../modules/stores';
import { ActionModal, ModalTypes } from '@monorepo/controlled/src/components/modal/modals/action-modal/action-modal';
import { DispatchLogsTypes, useLogs } from '@monorepo/controlled/src/hooks/use-logs';
import { ErrorMsg } from '../../../modules/components/error-msg/error-msg';
import { DropdownNew } from '@monorepo/base/src/components/dropdown-new/dropdown-new';
import { SelectOptions } from '@monorepo/base/src/components/select-new/select-new';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { BarLoader } from '@monorepo/base/src/components/bar-loader/bar-loader';
import { Textarea } from '@monorepo/base/src/components/form/textarea/textarea';
import { formatAffectedCampaignsText, WebappsCidApi } from '../../../modules/apis/webapps-cid.api';
import { TargetCidModel } from '../../../modules/models/cid.model';
import { WebappsCidModel } from '../../../modules/models/webapps-cid.model';
import { IAskError } from '@monorepo/tools/src/lib/tools/ask/ask';
import { isAbortError } from '@monorepo/tools/src/lib/tools/ask/guards';
import { useList } from '../../../modules/hooks/tools/use-list';

const toWebappsCidList = (res: unknown): Partial<WebappsCidModel>[] =>
	Array.isArray(res) ? res : ((res as { cids?: Partial<WebappsCidModel>[] })?.cids ?? []);

const getAskErrorMessage = (err: IAskError, fallback: string): string => {
	const message = err?.data?.message;
	if (typeof message === 'string') {
		return message;
	}
	if (message && typeof message === 'object' && 'message' in message && typeof message.message === 'string') {
		return message.message;
	}
	if (err?.response?.status) {
		return `${fallback} (${err.response.status})`;
	}
	return fallback;
};

const toWebappsCidOptions = (cids: Partial<WebappsCidModel>[] | { cid: id; name?: string }[]): SelectOptions[] =>
	cids.map(item => {
		const cid = 'getCid' in item && item.getCid ? item.getCid() : (item as { cid: id }).cid;
		const name = 'getName' in item && item.getName ? item.getName() : (item as { name?: string }).name;
		return {
			label: `${cid} (${name || 'Unknown'})`,
			value: cid,
		};
	});

const getCidLabel = (cids: WebappsCidModel[], cidValue?: id) => {
	if (!cidValue) {
		return '';
	}
	const match = cids.find(item => String(item.getCid()) === String(cidValue));
	return match ? `${match.getCid()} (${match.getName() || 'Unknown'})` : String(cidValue);
};

export const ToolsReplaceSegmentationCids = observer(() => {
	const { cidsStore, modalsStore, webappsCidStore } = useStores<IAdminxStore>();
	const cidsCrudStore = cidsStore.getCrud();
	const cid = cidsCrudStore.getData();
	const formStore = cidsCrudStore.getFormStore();
	const isValidForm = formStore.getIsValid();
	const isSuccess = cidsCrudStore.getIsSuccess();
	const error = cidsCrudStore.getHttpError();
	const [isFormClicked, setFormClicked] = useState<boolean>(false);
	const [linkedCids, setLinkedCids] = useState<WebappsCidModel[]>([]);
	const [cidOptions, setCidOptions] = useState<WebappsCidModel[]>([]);
	const [rowCidOptions, setRowCidOptions] = useState<Record<string, WebappsCidModel[]>>({});
	const [loadingRowCids, setLoadingRowCids] = useState<Record<string, boolean>>({});
	const [isLoadingLinked, setIsLoadingLinked] = useState(false);
	const [isLoadingCids, setIsLoadingCids] = useState(false);
	const [isLoadingSegments, setIsLoadingSegments] = useState(false);
	const [affectedCampaignsText, setAffectedCampaignsText] = useState('');
	const [isLoadingAffectedCampaigns, setIsLoadingAffectedCampaigns] = useState(false);
	const affectedCampaignsRequestRef = useRef(0);

	useList<WebappsCidModel>(webappsCidStore.listStore);

	const { dispatchLog } = useLogs();

	const cidLabelLookup = useMemo(() => {
		const byCid = new Map<string, WebappsCidModel>();
		[...linkedCids, ...webappsCidStore.listStore.getList()].forEach(item => {
			byCid.set(String(item.getCid()), item);
		});
		return Array.from(byCid.values());
	}, [linkedCids, webappsCidStore.listStore.getList()]);

	const linkedCidOptions = useMemo(() => toWebappsCidOptions(linkedCids), [linkedCids]);
	const replaceCidOptions = useMemo(() => toWebappsCidOptions(cidOptions), [cidOptions]);
	const segmentRows = cid.getSegmentRows();
	const canSubmit =
		Boolean(cid.getCurrentCid()) &&
		Boolean(cid.getTargetCid()) &&
		(segmentRows.length === 0 || segmentRows.every(row => Boolean(row.getNewTargetCid())));

	useEffect(() => {
		let cancelled = false;
		setIsLoadingLinked(true);
		WebappsCidApi.linkedCids()
			.then(res => {
				if (cancelled) {
					return;
				}
				setLinkedCids(toWebappsCidList(res).map(item => new WebappsCidModel(item)));
			})
			.catch((err: IAskError) => {
				if (cancelled || isAbortError(err)) {
					return;
				}
				dispatchLog({
					msg: getAskErrorMessage(err, "Couldn't load linked CIDs"),
					type: DispatchLogsTypes.Error,
				});
				setLinkedCids([]);
			})
			.finally(() => {
				if (!cancelled) {
					setIsLoadingLinked(false);
				}
			});
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		if (!isValidForm && isFormClicked) {
			dispatchLog({
				msg: 'Please complete all mandatory fields',
				type: DispatchLogsTypes.Error,
			});
		}
	}, [isFormClicked]);

	useEffect(() => {
		if (isSuccess) {
			dispatchLog({
				msg: `CID was updated successfully`,
				type: DispatchLogsTypes.Success,
			});
			cidsCrudStore.setIsSuccess(false);
			cidsCrudStore.reset();
			setCidOptions([]);
			setRowCidOptions({});
			setLoadingRowCids({});
			setAffectedCampaignsText('');
		}
	}, [isSuccess]);

	const loadRowCidOptions = (targetCid: id) => {
		const rowKey = String(targetCid);
		setLoadingRowCids(prev => ({ ...prev, [rowKey]: true }));
		return WebappsCidApi.getCids(targetCid)
			.then(res =>
				setRowCidOptions(prev => ({
					...prev,
					[rowKey]: toWebappsCidList(res).map(item => new WebappsCidModel(item)),
				}))
			)
			.catch((err: IAskError) => {
				if (isAbortError(err)) {
					return;
				}
				dispatchLog({
					msg: getAskErrorMessage(err, `Couldn't load CIDs for target ${targetCid}`),
					type: DispatchLogsTypes.Error,
				});
				setRowCidOptions(prev => ({ ...prev, [rowKey]: [] }));
			})
			.finally(() => setLoadingRowCids(prev => ({ ...prev, [rowKey]: false })));
	};

	const loadAllRowCidOptions = (rows: TargetCidModel[]) => {
		const uniqueTargetCids = [
			...new Set(rows.map(row => row.getCurrentTargetCid()).filter((targetCid): targetCid is id => Boolean(targetCid))),
		];
		uniqueTargetCids.forEach(targetCid => loadRowCidOptions(targetCid));
	};

	const loadCidOptions = (currentCid: id) => {
		setIsLoadingCids(true);
		return WebappsCidApi.getCids(currentCid)
			.then(res => setCidOptions(toWebappsCidList(res).map(item => new WebappsCidModel(item))))
			.catch((err: IAskError) => {
				if (isAbortError(err)) {
					return;
				}
				dispatchLog({
					msg: getAskErrorMessage(err, "Couldn't load target CIDs"),
					type: DispatchLogsTypes.Error,
				});
				setCidOptions([]);
			})
			.finally(() => setIsLoadingCids(false));
	};

	const loadSegmentRows = (currentCid: id) => {
		setIsLoadingSegments(true);
		const params = new URLSearchParams();
		params.set('source_cid', String(currentCid));
		return WebappsCidApi.getSegments(currentCid, params)
			.then(res => {
				const rows = (res.cid_segments || [])
					.filter(segment => segment.target_cid != null && segment.target_cid !== '')
					.map(
						segment =>
							new TargetCidModel({
								current_cid: segment.target_cid,
							})
					);
				runInAction(() => cid.setSegmentRows(rows));
				setRowCidOptions({});
				setLoadingRowCids({});
				loadAllRowCidOptions(rows);
			})
			.catch((err: IAskError) => {
				dispatchLog({
					msg: getAskErrorMessage(err, "Couldn't load segments"),
					type: DispatchLogsTypes.Error,
				});
				runInAction(() => cid.setSegmentRows([]));
				setRowCidOptions({});
				setLoadingRowCids({});
			})
			.finally(() => setIsLoadingSegments(false));
	};

	const loadAffectedCampaigns = (currentCid: id) => {
		const requestId = ++affectedCampaignsRequestRef.current;
		setIsLoadingAffectedCampaigns(true);
		setAffectedCampaignsText('');

		return WebappsCidApi.getCampaigns(currentCid)
			.then(campaigns => {
				if (requestId !== affectedCampaignsRequestRef.current) {
					return;
				}

				if (!campaigns.length) {
					setAffectedCampaignsText('No campaigns affected');
					return;
				}

				setAffectedCampaignsText(formatAffectedCampaignsText(campaigns));
			})
			.catch((err: IAskError) => {
				if (requestId !== affectedCampaignsRequestRef.current || isAbortError(err)) {
					return;
				}

				dispatchLog({
					msg: getAskErrorMessage(err, "Couldn't load affected campaigns"),
					type: DispatchLogsTypes.Error,
				});
				setAffectedCampaignsText('');
			})
			.finally(() => {
				if (requestId === affectedCampaignsRequestRef.current) {
					setIsLoadingAffectedCampaigns(false);
				}
			});
	};

	const onSelectCurrentCid = (values: id[]) => {
		const currentCid = values[0];
		formStore.clearError('current_cid');
		cid.setCurrentCid(currentCid);
		if (!currentCid) {
			affectedCampaignsRequestRef.current += 1;
			setAffectedCampaignsText('');
			setIsLoadingAffectedCampaigns(false);
			return;
		}
		setCidOptions([]);
		setRowCidOptions({});
		setLoadingRowCids({});
		loadCidOptions(currentCid);
		loadSegmentRows(currentCid);
		loadAffectedCampaigns(currentCid);
	};

	const onSelectTargetCid = (values: id[]) => {
		cid.setTargetCid(values[0]);
		formStore.clearError('target_cid');
	};

	const onSelectNewCid = (values: id[], index: number) => {
		segmentRows[index]?.setNewTargetCid(values[0]);
		formStore.clearError(`segments[${index}].new_cid`);
	};

	const onSubmitCids = () => {
		setFormClicked(true);
		if (cidsCrudStore.isValid()) {
			modalsStore.addModal({
				children: (
					<ActionModal
						type={ModalTypes.Warning}
						primaryBtnTitle={'Yes, I am sure'}
						onAction={() => {
							cidsCrudStore.create();
						}}>
						<Fragment>Caution: You are about to update a CID which might be active, are you sure?</Fragment>
					</ActionModal>
				),
			});
		}
	};

	const isLoadingRowCids = Object.values(loadingRowCids).some(Boolean);
	const isPageLoading = isLoadingLinked || isLoadingCids || isLoadingSegments || isLoadingRowCids || isLoadingAffectedCampaigns;

	return (
		<Page>
			<div className={styles.formWrapper}>
				<div className={styles.pagebar}>
					<Pagebar>
						<PagebarTitle>Replace CID</PagebarTitle>
					</Pagebar>
				</div>

				<div className={styles.title}>
					<h2>Source CID</h2>
					<sub>Source CID will effect campaigns destination url</sub>
				</div>

				<Card className={styles.sourceCard}>
					<div className={styles.row}>
						<DropdownNew
							label={'Current CID'}
							classNames={{
								dropdownWrapper: styles.dropdownWrapper,
								parentSelectWrapper: styles.parentSelectWrapper,
								dropdownInput: styles.dropdownInput,
							}}
							options={linkedCidOptions}
							defaultValues={cid.getCurrentCid() ? [cid.getCurrentCid()!] : ([] as id[])}
							autocomplete={true}
							autocompletePlaceholder={'Search Current CID'}
							multi={false}
							required
							onSelect={onSelectCurrentCid}
							disabled={isLoadingLinked}
						/>
						<DropdownNew
							label={'Target CID'}
							classNames={{
								dropdownWrapper: styles.dropdownWrapper,
								parentSelectWrapper: styles.parentSelectWrapper,
								dropdownInput: styles.dropdownInput,
							}}
							options={replaceCidOptions}
							defaultValues={cid.getTargetCid() ? [cid.getTargetCid()!] : ([] as id[])}
							autocomplete={true}
							autocompletePlaceholder={'Search Target CID'}
							multi={false}
							required
							onSelect={onSelectTargetCid}
							disabled={!cid.getCurrentCid() || isLoadingCids}
						/>
					</div>
					{cid.getCurrentCid() ? (
						<Textarea
							wrapperClassName={styles.affectedCampaignsWrapper}
							inputWrapperClassName={styles.affectedCampaignsTextareaWrapper}
							placeholder={isLoadingAffectedCampaigns ? 'Loading affected campaigns...' : 'Campaigns affected by this change'}
							value={affectedCampaignsText}
							disabled={true}
							rows={6}
						/>
					) : null}
					<div className={styles.limitLoaderSize}>
						<BarLoader is={isPageLoading} />
					</div>
				</Card>

				{segmentRows.length > 0 ? (
					<Fragment>
						<div className={styles.title}>
							<h2>Target CIDs</h2>
							<sub>Target CID will effect domains grouping table</sub>
						</div>

						<Card className={styles.targetCard}>
							{segmentRows.map((row, index) => {
								const rowTargetCid = row.getCurrentTargetCid();
								const rowKey = rowTargetCid ? String(rowTargetCid) : '';
								const rowTargetLabel = getCidLabel(cidLabelLookup, rowTargetCid);

								return (
									<div className={`${styles.row} ${styles.error}`} key={index}>
										<span className={styles.groupNumber}>{rowTargetLabel || `Target CID group ${index + 1}`}</span>
										<DropdownNew
											label={'New CID'}
											classNames={{
												dropdownWrapper: styles.dropdownWrapper,
												parentSelectWrapper: styles.parentSelectWrapper,
												dropdownInput: styles.dropdownInput,
											}}
											options={toWebappsCidOptions(rowCidOptions[rowKey] || [])}
											defaultValues={row.getNewTargetCid() ? [row.getNewTargetCid()!] : ([] as id[])}
											autocomplete={true}
											autocompletePlaceholder={'Search New CID'}
											multi={false}
											required
											onSelect={val => onSelectNewCid(val, index)}
											disabled={!rowTargetCid || loadingRowCids[rowKey]}
										/>
									</div>
								);
							})}
						</Card>
					</Fragment>
				) : cid.getCurrentCid() && !isLoadingSegments ? (
					<Card title={'No Segmentations Found'}> </Card>
				) : null}
			</div>
			<BottomBar>
				{error ? <ErrorMsg error={error} /> : null}
				<BottomBarActions>
					<PrimaryButton disabled={!canSubmit} onClick={() => onSubmitCids()}>
						Submit
					</PrimaryButton>
				</BottomBarActions>
			</BottomBar>
		</Page>
	);
});
